import React, { useEffect, useState } from "react";
import { api, formatPLN, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, Save, Repeat, Zap } from "lucide-react";
import { toast } from "sonner";

const TYPES = {
  fixed: { label: "Stałe", icon: Repeat },
  variable: { label: "Zmienne", icon: Zap },
};

export default function Costs() {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("fixed");
  const [draft, setDraft] = useState({ name: "", amount: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/costs").then((r) => setCosts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = costs.filter((c) => c.type === tab);
  const fixedTotal = costs
    .filter((c) => c.type === "fixed")
    .reduce((s, c) => s + Number(c.amount || 0), 0);
  const variableTotal = costs
    .filter((c) => c.type === "variable")
    .reduce((s, c) => s + Number(c.amount || 0), 0);

  const add = async () => {
    if (!draft.name.trim()) {
      toast.error("Podaj nazwę kosztu");
      return;
    }
    setSaving(true);
    try {
      await api.post("/costs", {
        name: draft.name.trim(),
        amount: Number(draft.amount || 0),
        notes: draft.notes,
        type: tab,
      });
      setDraft({ name: "", amount: "", notes: "" });
      toast.success("Koszt dodany");
      load();
    } catch {
      toast.error("Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  const updateField = async (id, patch) => {
    try {
      await api.put(`/costs/${id}`, patch);
      load();
    } catch {
      toast.error("Błąd zapisu");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/costs/${id}`);
      toast.success("Usunięto");
      load();
    } catch {
      toast.error("Błąd");
    }
  };

  return (
    <div className="space-y-6" data-testid="costs-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
          Koszty ogólne
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
          Koszty działalności
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-2">
          Stałe i zmienne koszty firmy poza pojedynczym zleceniem.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Tot label="Stałe" value={formatPLN(fixedTotal)} accent="var(--primary)" />
        <Tot label="Zmienne" value={formatPLN(variableTotal)} accent="#3B5249" />
        <Tot
          label="Razem"
          value={formatPLN(fixedTotal + variableTotal)}
          accent="#1A1A1A"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="costs-tabs">
          <TabsTrigger value="fixed" data-testid="tab-fixed">Stałe</TabsTrigger>
          <TabsTrigger value="variable" data-testid="tab-variable">Zmienne</TabsTrigger>
        </TabsList>

        {["fixed", "variable"].map((t) => (
          <TabsContent key={t} value={t} className="space-y-4 mt-4">
            <div className="surface p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid={`new-cost-form-${t}`}>
              <div className="md:col-span-5 space-y-1.5">
                <Label htmlFor={`name-${t}`}>Nazwa</Label>
                <Input
                  id={`name-${t}`}
                  data-testid="new-cost-name"
                  value={draft.name}
                  placeholder={t === "fixed" ? "np. Czynsz, leasing, abonament" : "np. Prąd, internet, biuro"}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor={`amount-${t}`}>Kwota (PLN)</Label>
                <Input
                  id={`amount-${t}`}
                  data-testid="new-cost-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.amount}
                  placeholder="0"
                  onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor={`notes-${t}`}>Notatka</Label>
                <Input
                  id={`notes-${t}`}
                  data-testid="new-cost-notes"
                  value={draft.notes}
                  placeholder="opcjonalnie"
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  onClick={add}
                  disabled={saving}
                  data-testid="add-cost-btn"
                  className="w-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                >
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="surface p-10 text-center text-[var(--text-muted)]">Wczytywanie...</div>
            ) : filtered.length === 0 ? (
              <div className="surface p-12 text-center" data-testid={`empty-${t}`}>
                <div className="heading text-lg font-semibold">
                  Brak kosztów {t === "fixed" ? "stałych" : "zmiennych"}
                </div>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Dodaj pierwszy koszt powyżej.
                </p>
              </div>
            ) : (
              <div className="surface overflow-hidden" data-testid={`costs-list-${t}`}>
                <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)] bg-[var(--surface-2)]/40">
                  <div className="col-span-5">Nazwa</div>
                  <div className="col-span-3">Notatka</div>
                  <div className="col-span-2 text-right">Kwota</div>
                  <div className="col-span-1 text-right">Data</div>
                  <div className="col-span-1"></div>
                </div>
                {filtered.map((c) => (
                  <CostRow
                    key={c.id}
                    cost={c}
                    onUpdate={updateField}
                    onRemove={remove}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function CostRow({ cost, onUpdate, onRemove }) {
  const [name, setName] = useState(cost.name);
  const [amount, setAmount] = useState(cost.amount);
  const [notes, setNotes] = useState(cost.notes || "");

  return (
    <div
      className="grid grid-cols-12 items-center px-5 py-2 border-b last:border-0 border-[var(--border)] gap-2"
      data-testid={`cost-row-${cost.id}`}
    >
      <div className="col-span-5">
        <input
          className="cost-input text-left"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name !== cost.name && onUpdate(cost.id, { name })}
          data-testid={`cost-edit-name-${cost.id}`}
        />
      </div>
      <div className="col-span-3">
        <input
          className="cost-input text-left"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => notes !== (cost.notes || "") && onUpdate(cost.id, { notes })}
          data-testid={`cost-edit-notes-${cost.id}`}
        />
      </div>
      <div className="col-span-2">
        <input
          type="number"
          step="0.01"
          min="0"
          className="cost-input"
          value={amount === 0 ? "" : amount}
          placeholder="0"
          onChange={(e) =>
            setAmount(e.target.value === "" ? 0 : Number(e.target.value))
          }
          onBlur={() => Number(amount) !== Number(cost.amount) && onUpdate(cost.id, { amount: Number(amount || 0) })}
          data-testid={`cost-edit-amount-${cost.id}`}
        />
      </div>
      <div className="col-span-1 text-right text-xs text-[var(--text-muted)]">
        {formatDate(cost.created_at)}
      </div>
      <div className="col-span-1 text-right">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="text-[var(--text-muted)] hover:text-[var(--danger)] transition-colors p-1"
              data-testid={`cost-delete-${cost.id}`}
              aria-label="Usuń koszt"
            >
              <Trash2 size={16} />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Usunąć koszt?</AlertDialogTitle>
              <AlertDialogDescription>
                Pozycja "{cost.name}" zostanie trwale usunięta.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anuluj</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onRemove(cost.id)}
                className="bg-[var(--danger)] hover:bg-[var(--danger)]/90"
                data-testid={`cost-delete-confirm-${cost.id}`}
              >
                Usuń
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function Tot({ label, value, accent }) {
  return (
    <div className="surface p-5">
      <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide">{label}</div>
      <div
        className="heading text-2xl font-bold mt-2 tabular-nums"
        style={accent ? { color: accent } : {}}
      >
        {value}
      </div>
    </div>
  );
}
